import { z } from "zod";
import { createEndpoint, createEndpointWithPagination } from "../createEndpoint";
import type { HttpClient, QueryParams } from "../httpClient";
import {
    GenerateExpertAiBatchRequestSchema,
    ExpertAiBatchTriggerResponseSchema,
    ExpertAiBatchStatusSchema,
    ExpertAiContextSchema,
    ExpertAiPublishedSummarySchema,
    ExpertAiStepRunDetailSchema,
    ExpertAiStepRunListMetaSchema,
    ExpertAiStepRunSchema,
    ExpertAiSummaryOverviewSchema,
    ExpertAiTriggerResponseSchema,
    PublishExpertAiSummaryRequestSchema,
    RegenerateExpertAiStepRequestSchema,
    UpdateExpertAiContextRequestSchema,
    type AiStepRunStatus,
    type AiSummaryKind,
    type AiSummaryStep,
} from "../schemas/expert-ai-summaries";

export interface AdminExpertAiStepRunsQueryParams extends QueryParams {
    step?: AiSummaryStep;
    status?: AiStepRunStatus;
    limit?: number;
    cursor?: string;
}

export function createAdminExpertAiSummariesApi(http: HttpClient, baseUrl: string) {
    const adminExpertsBaseUrl = `${baseUrl}/admin/experts`;

    return {
        listActiveBatches: createEndpoint<z.infer<typeof ExpertAiBatchStatusSchema>[]>(
            http, "GET", "/ai-summaries/batches", {
                base: `${baseUrl}/admin`,
                responseSchema: z.array(ExpertAiBatchStatusSchema),
            },
        ),

        getBatch: createEndpoint<
            z.infer<typeof ExpertAiBatchStatusSchema>, unknown, QueryParams, { batchId: string }
        >(http, "GET", "/ai-summaries/batches/:batchId", {
            base: `${baseUrl}/admin`,
            responseSchema: ExpertAiBatchStatusSchema,
            params: ["batchId"],
        }),

        generateBatch: createEndpoint<
            z.infer<typeof ExpertAiBatchTriggerResponseSchema>,
            z.input<typeof GenerateExpertAiBatchRequestSchema>
        >(http, "POST", "/ai-summaries/generate", {
            base: `${baseUrl}/admin`,
            responseSchema: ExpertAiBatchTriggerResponseSchema,
        }),

        getContext: createEndpoint<
            z.infer<typeof ExpertAiContextSchema>,
            unknown,
            QueryParams,
            { expertId: number }
        >(http, "GET", "/:expertId/ai-context", {
            base: adminExpertsBaseUrl,
            responseSchema: ExpertAiContextSchema,
            params: ["expertId"],
        }),

        updateContext: createEndpoint<
            z.infer<typeof ExpertAiContextSchema>,
            z.input<typeof UpdateExpertAiContextRequestSchema>,
            QueryParams,
            { expertId: number }
        >(http, "PUT", "/:expertId/ai-context", {
            base: adminExpertsBaseUrl,
            responseSchema: ExpertAiContextSchema,
            params: ["expertId"],
        }),

        getOverview: createEndpoint<
            z.infer<typeof ExpertAiSummaryOverviewSchema>,
            unknown,
            QueryParams,
            { expertId: number }
        >(http, "GET", "/:expertId/ai-summaries", {
            base: adminExpertsBaseUrl,
            responseSchema: ExpertAiSummaryOverviewSchema,
            params: ["expertId"],
        }),

        listRuns: createEndpointWithPagination<
            z.infer<typeof ExpertAiStepRunSchema>,
            z.infer<typeof ExpertAiStepRunListMetaSchema>,
            unknown,
            AdminExpertAiStepRunsQueryParams,
            { expertId: number }
        >(http, "GET", "/:expertId/ai-summaries/runs", {
            base: adminExpertsBaseUrl,
            itemSchema: ExpertAiStepRunSchema,
            metaSchema: ExpertAiStepRunListMetaSchema,
            params: ["expertId"],
        }),

        getRun: createEndpoint<
            z.infer<typeof ExpertAiStepRunDetailSchema>,
            unknown,
            QueryParams,
            { expertId: number; runId: number }
        >(http, "GET", "/:expertId/ai-summaries/runs/:runId", {
            base: adminExpertsBaseUrl,
            responseSchema: ExpertAiStepRunDetailSchema,
            params: ["expertId", "runId"],
        }),

        generate: createEndpoint<
            z.infer<typeof ExpertAiTriggerResponseSchema>,
            unknown,
            QueryParams,
            { expertId: number }
        >(http, "POST", "/:expertId/ai-summaries/generate", {
            base: adminExpertsBaseUrl,
            responseSchema: ExpertAiTriggerResponseSchema,
            params: ["expertId"],
        }),

        regenerateStep: createEndpoint<
            z.infer<typeof ExpertAiTriggerResponseSchema>,
            z.input<typeof RegenerateExpertAiStepRequestSchema>,
            QueryParams,
            { expertId: number; step: AiSummaryStep }
        >(http, "POST", "/:expertId/ai-summaries/steps/:step/regenerate", {
            base: adminExpertsBaseUrl,
            responseSchema: ExpertAiTriggerResponseSchema,
            params: ["expertId", "step"],
        }),

        publish: createEndpoint<
            z.infer<typeof ExpertAiPublishedSummarySchema>,
            z.input<typeof PublishExpertAiSummaryRequestSchema>,
            QueryParams,
            { expertId: number; kind: AiSummaryKind }
        >(http, "POST", "/:expertId/ai-summaries/:kind/publish", {
            base: adminExpertsBaseUrl,
            responseSchema: ExpertAiPublishedSummarySchema,
            params: ["expertId", "kind"],
        }),
    };
}

export type AdminExpertAiSummariesApi = ReturnType<typeof createAdminExpertAiSummariesApi>;
